// src/pages/Admin/Users.jsx
// ✅ Full communication support: Notify any user (seller/buyer) via WhatsApp + Email
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Avatar,
  Select, FormControl, InputLabel, MenuItem, Tooltip, LinearProgress,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Divider,
} from '@mui/material';
import {
  Search, Refresh, Visibility, CheckCircle, Cancel,
  Person, VerifiedUser, Block, SupervisorAccount, PersonAdd,
  NotificationsActive, WhatsApp, Email, Phone, Store,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

// ── Default messages per user type ───────────────────────────────────────────
const defaultMessage = (user, topic) => {
  const name = user.company_name || user.contact_person || user.email;
  if (user.user_type === 'seller') {
    const msgs = {
      invoice:     `Dear ${name},\n\nPlease find your invoice details for the recent transaction. Kindly review and confirm the invoice at your earliest convenience.\n\nRegards,\nSmartwave International`,
      payment:     `Dear ${name},\n\nWe are pleased to confirm that your payment has been received and processed successfully. Thank you for your prompt payment.\n\nRegards,\nSmartwave International`,
      shipment:    `Dear ${name},\n\nYour shipment has been dispatched. Please find the tracking details in your dashboard.\n\nRegards,\nSmartwave International`,
      general:     `Dear ${name},\n\nWe have an important update regarding your account with Smartwave International. Please log in to your portal to review the latest information.\n\nRegards,\nSmartwave International`,
      kyc:         `Dear ${name},\n\nYour KYC documents are currently under review. We may require additional information. Please log in to check the status.\n\nRegards,\nSmartwave International`,
    };
    return msgs[topic] || msgs.general;
  }
  // buyer
  const msgs = {
    invoice:     `Dear ${name},\n\nYour invoice has been generated. Please log in to your buyer portal to view and download the invoice.\n\nRegards,\nSmartwave International`,
    payment:     `Dear ${name},\n\nYour payment has been successfully verified. Your order is now being processed.\n\nRegards,\nSmartwave International`,
    shipment:    `Dear ${name},\n\nYour shipment is on its way! Please log in to track your order in real time.\n\nRegards,\nSmartwave International`,
    general:     `Dear ${name},\n\nWe have an update on your recent enquiry. Please log in to your buyer portal to view the latest details.\n\nRegards,\nSmartwave International`,
    kyc:         `Dear ${name},\n\nYour KYC verification is complete. You can now place orders on our platform.\n\nRegards,\nSmartwave International`,
  };
  return msgs[topic] || msgs.general;
};

const TOPICS = [
  { value: 'general',  label: 'General Update' },
  { value: 'invoice',  label: 'Invoice' },
  { value: 'payment',  label: 'Payment Confirmation' },
  { value: 'shipment', label: 'Shipment Update' },
  { value: 'kyc',      label: 'KYC / Verification' },
];

const AdminUsers = () => {
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [tabValue,     setTabValue]     = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialog,   setViewDialog]   = useState(false);


  // ── Notify state ──────────────────────────────────────────────────────────
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [notifyUser,   setNotifyUser]   = useState(null);
  const [notifyTopic,  setNotifyTopic]  = useState('general');
  const [notifyMsg,    setNotifyMsg]    = useState('');
  const [notifyResult, setNotifyResult] = useState(null);
  const [notifying,    setNotifying]    = useState(false);

  const TAB_TYPE_MAP = ['all', 'seller', 'buyer', 'admin'];

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers();
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.users || response.data?.data || []);
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await adminAPI.verifyUser(userId);
      toast.success('User verified successfully');
      fetchUsers();
    } catch { toast.error('Failed to verify user'); }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch { toast.error('Failed to update status'); }
  };

  // ── Open notify dialog ────────────────────────────────────────────────────
  const openNotify = (user, topic = 'general') => {
    setNotifyUser(user);
    setNotifyTopic(topic);
    setNotifyMsg(defaultMessage(user, topic));
    setNotifyResult(null);
    setNotifyDialog(true);
  };

  const handleTopicChange = (topic) => {
    setNotifyTopic(topic);
    setNotifyMsg(defaultMessage(notifyUser, topic));
  };

  const handleNotify = async () => {
    if (!notifyUser) return;
    setNotifying(true);
    try {
      const res = await adminAPI.notifyUser({
        buyer_id: notifyUser.id,   // backend param name is buyer_id but works for any user
        message:  notifyMsg,
        type:     notifyTopic,
      });
      setNotifyResult(res.data);
      toast.success('Contact details ready!');
    } catch {
      toast.error('Failed to get contact details');
    } finally {
      setNotifying(false);
    }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    total:    users.length,
    sellers:  users.filter(u => u.user_type === 'seller').length,
    buyers:   users.filter(u => u.user_type === 'buyer').length,
    verified: users.filter(u => u.verified).length,
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const userType = TAB_TYPE_MAP[tabValue];
  const filteredUsers = users.filter(u => {
    const matchesType   = userType === 'all' || u.user_type === userType;
    const matchesStatus =
      statusFilter === 'all'          ? true :
      statusFilter === 'verified'     ? u.verified :
      statusFilter === 'not_verified' ? !u.verified : true;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      (u.email        || '').toLowerCase().includes(q) ||
      (u.company_name || '').toLowerCase().includes(q) ||
      (u.phone        || '').toLowerCase().includes(q);
    return matchesType && matchesStatus && matchesSearch;
  });

  const getRoleColor = (type) =>
    type === 'admin' ? 'error' : type === 'seller' ? 'warning' : 'primary';

  const getRoleIcon = (type) =>
    type === 'seller' ? <Store fontSize="small" /> :
    type === 'admin'  ? <VerifiedUser fontSize="small" /> :
    <Person fontSize="small" />;

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading users...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Users Management</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all accounts. Use the 🔔 icon to contact any seller or buyer directly.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchUsers}>Refresh</Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Users',  value: stats.total,    color: '#2196f3', icon: <SupervisorAccount /> },
          { label: 'Sellers',      value: stats.sellers,  color: '#ff9800', icon: <Store />,         textColor: 'warning.main' },
          { label: 'Buyers',       value: stats.buyers,   color: '#4caf50', icon: <Person />,        textColor: 'success.main' },
          { label: 'Verified',     value: stats.verified, color: '#00bcd4', icon: <VerifiedUser />,  textColor: 'info.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold" color={s.textColor || 'text.primary'}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs — Sellers first for easy access */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          <Tab label="All Users" icon={<SupervisorAccount />} iconPosition="start" />
          <Tab label={`Sellers (${stats.sellers})`} icon={<Store />} iconPosition="start" />
          <Tab label={`Buyers (${stats.buyers})`}   icon={<Person />} iconPosition="start" />
          <Tab label="Admins"    icon={<VerifiedUser />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small"
              placeholder="Search by name, email, phone or company..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification</InputLabel>
              <Select label="Verification" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="not_verified">Not Verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['User / Company', 'Role', 'Contact', 'Verification', 'Status', 'Joined', 'Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Person sx={{ fontSize: 60, color: 'text.secondary', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography color="text.secondary">
                      {users.length === 0 ? 'No users found' : 'No users match the filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.map((u) => (
                <TableRow key={u.id} hover>

                  {/* User / Company */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{
                        bgcolor: u.user_type === 'seller' ? 'warning.light' :
                                 u.user_type === 'admin'  ? 'error.light'   : 'primary.light',
                        color:   u.user_type === 'seller' ? 'warning.dark'  :
                                 u.user_type === 'admin'  ? 'error.dark'    : 'primary.dark',
                      }}>
                        {(u.company_name || u.email || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {u.company_name || u.contact_person || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Role */}
                  <TableCell>
                    <Chip label={u.user_type || 'buyer'} size="small"
                      color={getRoleColor(u.user_type)} variant="outlined"
                      icon={getRoleIcon(u.user_type)}
                      sx={{ textTransform: 'capitalize' }} />
                  </TableCell>

                  {/* Contact info */}
                  <TableCell>
                    {u.phone ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption">{u.phone}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">No phone</Typography>
                    )}
                  </TableCell>

                  {/* Verification */}
                  <TableCell>
                    <Chip label={u.verified ? 'Verified' : 'Pending'} size="small"
                      color={u.verified ? 'success' : 'warning'}
                      icon={u.verified ? <CheckCircle fontSize="small" /> : undefined}
                    />
                  </TableCell>

                  {/* Active status */}
                  <TableCell>
                    <Chip
                      label={u.is_active !== false && u.active !== false ? 'Active' : 'Inactive'}
                      size="small"
                      color={u.is_active !== false && u.active !== false ? 'success' : 'default'}
                    />
                  </TableCell>

                  {/* Joined */}
                  <TableCell>
                    <Typography variant="body2">{formatDate(u.created_at)}</Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary"
                          onClick={() => { setSelectedUser(u); setViewDialog(true); }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Notify / Contact ${u.user_type === 'seller' ? 'Seller' : 'Buyer'}`}>
                        <IconButton size="small" color="secondary" onClick={() => openNotify(u)}>
                          <NotificationsActive />
                        </IconButton>
                      </Tooltip>
                      {!u.verified && (
                        <Tooltip title="Verify User">
                          <IconButton size="small" color="success" onClick={() => handleVerifyUser(u.id)}>
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={u.is_active !== false && u.active !== false ? 'Deactivate' : 'Activate'}>
                        <IconButton size="small"
                          color={u.is_active !== false && u.active !== false ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(u.id)}>
                          {u.is_active !== false && u.active !== false ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredUsers.length} of {users.length} users
          </Typography>
        </Box>
      </Paper>

      {/* ── View User Dialog ── */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        {selectedUser && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {selectedUser.user_type === 'seller' ? '🏪 Seller Details' : '👤 User Details'}
                </Typography>
                <Chip label={selectedUser.user_type} size="small"
                  color={getRoleColor(selectedUser.user_type)} sx={{ textTransform: 'capitalize' }} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {[
                  ['Company',   selectedUser.company_name],
                  ['Contact',   selectedUser.contact_person],
                  ['Email',     selectedUser.email],
                  ['Phone',     selectedUser.phone],
                  ['Country',   selectedUser.country],
                  ['Verified',  selectedUser.verified ? '✅ Yes' : '⏳ Pending'],
                  ['Status',    (selectedUser.is_active !== false && selectedUser.active !== false) ? '🟢 Active' : '🔴 Inactive'],
                  ['Joined',    formatDate(selectedUser.created_at)],
                ].map(([label, value]) => value ? (
                  <Box key={label} sx={{ display: 'flex', gap: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ width: 100, flexShrink: 0, color: 'text.secondary' }}>
                      {label}:
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              {/* Quick contact buttons */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Quick Contact
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button variant="outlined" color="success" startIcon={<WhatsApp />}
                    disabled={!selectedUser.phone}
                    onClick={() => {
                      const n = (selectedUser.phone || '').replace(/\D/g,'');
                      window.open(`https://wa.me/${n}`, '_blank');
                    }}>
                    {selectedUser.phone ? 'Open WhatsApp' : 'No Phone'}
                  </Button>
                  <Button variant="outlined" color="primary" startIcon={<Email />}
                    onClick={() => window.open(`mailto:${selectedUser.email}`, '_blank')}>
                    Send Email
                  </Button>
                  <Button variant="contained" color="secondary" startIcon={<NotificationsActive />}
                    onClick={() => { setViewDialog(false); openNotify(selectedUser); }}>
                    Compose Message
                  </Button>
                </Box>

                {/* Topic shortcuts for sellers */}
                {selectedUser.user_type === 'seller' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Quick message templates:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {TOPICS.map(t => (
                        <Chip key={t.value} label={t.label} size="small" clickable
                          variant="outlined" color="warning"
                          onClick={() => { setViewDialog(false); openNotify(selectedUser, t.value); }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Notify / Contact Dialog ── */}
      <Dialog open={notifyDialog}
        onClose={() => { setNotifyDialog(false); setNotifyResult(null); }}
        maxWidth="sm" fullWidth>
        {notifyUser && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <NotificationsActive color="secondary" />
                <Box>
                  <Typography variant="h6">
                    {notifyUser.user_type === 'seller' ? 'Contact Seller' : 'Notify Buyer'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notifyUser.company_name || notifyUser.email}
                    {notifyUser.user_type === 'seller' && (
                      <Chip label="Seller" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Topic selector */}
                <FormControl fullWidth size="small">
                  <InputLabel>Message Topic</InputLabel>
                  <Select label="Message Topic" value={notifyTopic}
                    onChange={(e) => handleTopicChange(e.target.value)}>
                    {TOPICS.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Message */}
                <TextField fullWidth multiline rows={6}
                  label="Message"
                  value={notifyMsg}
                  onChange={e => setNotifyMsg(e.target.value)}
                />

                <Alert severity="info" sx={{ py: 0.5 }}>
                  Click <strong>"Get Contact Links"</strong> to get this person's WhatsApp chat link
                  and a pre-filled email. You send from your own WhatsApp / email app.
                </Alert>

                {/* Results after clicking Get Links */}
                {notifyResult && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Divider />
                    <Alert severity="success">Links ready — choose how to send:</Alert>

                    {notifyResult.whatsapp_link ? (
                      <Button variant="contained" color="success" startIcon={<WhatsApp />} fullWidth
                        onClick={() => window.open(notifyResult.whatsapp_link, '_blank')}>
                        Open WhatsApp Chat with {notifyResult.buyer_name || notifyUser.company_name}
                      </Button>
                    ) : (
                      <Alert severity="warning">
                        No phone number registered — WhatsApp not available for this user.
                      </Alert>
                    )}

                    {notifyResult.buyer_email && (
                      <Button variant="outlined" color="primary" startIcon={<Email />} fullWidth
                        onClick={() => window.open(
                          `mailto:${notifyResult.buyer_email}?subject=${encodeURIComponent(
                            TOPICS.find(t => t.value === notifyTopic)?.label || 'Update from Smartwave'
                          )}&body=${encodeURIComponent(notifyMsg)}`,
                          '_blank'
                        )}>
                        Send Email to {notifyResult.buyer_email}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setNotifyDialog(false); setNotifyResult(null); }}>Close</Button>
              {!notifyResult && (
                <Button variant="contained" color="secondary" startIcon={<NotificationsActive />}
                  onClick={handleNotify}
                  disabled={notifying || !notifyMsg.trim()}>
                  {notifying ? 'Loading...' : 'Get Contact Links'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>


    </Container>
  );
};

export default AdminUsers;
